import {AdminAPI, type UserUpdateResponse} from '../api/AdminAPI'
import {ApiUtil} from '../api/ApiUtil'
import type {AdminProductResponseItem, AdminUserResponseItem} from '../api/types'
import type {AdminProduct, AdminUser} from '../reducers/types'
import type {AppDispatch, GetStateFunc} from '../store'
import {ActionUtil} from "./ActionUtil";

/**
 * Administrative operations related action types.
 */
export enum AdminActionType {
  ADMIN_GET_PRODUCTS_PENDING = 'ADMIN_GET_PRODUCTS_PENDING',
  ADMIN_GET_PRODUCTS_FULFILLED = 'ADMIN_GET_PRODUCTS_FULFILLED',
  ADMIN_GET_PRODUCTS_REJECTED = 'ADMIN_GET_PRODUCTS_REJECTED',
  ADMIN_GET_USERS_PENDING = 'ADMIN_GET_USERS_PENDING',
  ADMIN_GET_USERS_FULFILLED = 'ADMIN_GET_USERS_FULFILLED',
  ADMIN_GET_USERS_REJECTED = 'ADMIN_GET_USERS_REJECTED',
  ADMIN_GET_NOT_AUTHORIZED_USERS_PENDING = 'ADMIN_GET_NOT_AUTHORIZED_USERS_PENDING',
  ADMIN_GET_NOT_AUTHORIZED_USERS_FULFILLED = 'ADMIN_GET_NOT_AUTHORIZED_USERS_FULFILLED',
  ADMIN_GET_NOT_AUTHORIZED_USERS_REJECTED = 'ADMIN_GET_NOT_AUTHORIZED_USERS_REJECTED',
  ADMIN_UPDATE_USER_ACCESS_PENDING = 'ADMIN_UPDATE_USER_ACCESS_PENDING',
  ADMIN_UPDATE_USER_ACCESS_FULFILLED = 'ADMIN_UPDATE_USER_ACCESS_FULFILLED',
  ADMIN_UPDATE_USER_ACCESS_REJECTED = 'ADMIN_UPDATE_USER_ACCESS_REJECTED',
  ADMIN_UPDATE_PRODUCT_PENDING = 'ADMIN_UPDATE_PRODUCT_PENDING',
  ADMIN_UPDATE_PRODUCT_FULFILLED = 'ADMIN_UPDATE_PRODUCT_FULFILLED',
  ADMIN_UPDATE_PRODUCT_REJECTED = 'ADMIN_UPDATE_PRODUCT_REJECTED'
}

interface FetchProducts {
  type: AdminActionType.ADMIN_GET_PRODUCTS_PENDING
}

interface FetchProductsSuccess {
  type: AdminActionType.ADMIN_GET_PRODUCTS_FULFILLED
  payload: AdminProduct[]
}

interface FetchProductsFailure {
  type: AdminActionType.ADMIN_GET_PRODUCTS_REJECTED
  payload: {
    error: string
  }
}

interface FetchUsers {
  type: AdminActionType.ADMIN_GET_USERS_PENDING
}

interface FetchUsersSuccess {
  type: AdminActionType.ADMIN_GET_USERS_FULFILLED
  payload: AdminUser[]
}

interface FetchUsersFailure {
  type: AdminActionType.ADMIN_GET_USERS_REJECTED
  payload: {
    error: string
  }
}

interface FetchNotAuthorizedUsers {
  type: AdminActionType.ADMIN_GET_NOT_AUTHORIZED_USERS_PENDING
}

interface FetchNotAuthorizedUsersSuccess {
  type: AdminActionType.ADMIN_GET_NOT_AUTHORIZED_USERS_FULFILLED
  payload: AdminUser[]
}

interface FetchNotAuthorizedUsersFailure {
  type: AdminActionType.ADMIN_GET_NOT_AUTHORIZED_USERS_REJECTED
  payload: {
    error: string
  }
}

interface UpdateUserAccess {
  type: AdminActionType.ADMIN_UPDATE_USER_ACCESS_PENDING
}

interface UpdateUserAccessSuccess {
  type: AdminActionType.ADMIN_UPDATE_USER_ACCESS_FULFILLED
  payload: AdminUser
}

interface UpdateUserAccessFailure {
  type: AdminActionType.ADMIN_UPDATE_USER_ACCESS_REJECTED
  payload: {
    error: string
  }
}

interface UpdateProduct {
  type: AdminActionType.ADMIN_UPDATE_PRODUCT_PENDING
}

interface UpdateProductSuccess {
  type: AdminActionType.ADMIN_UPDATE_PRODUCT_FULFILLED
  payload: AdminProduct
}

interface UpdateProductFailure {
  type: AdminActionType.ADMIN_UPDATE_PRODUCT_REJECTED
  payload: {
    error: string
  }
}

export type AdminAction =
  | FetchUsers
  | FetchUsersSuccess
  | FetchUsersFailure
  | FetchNotAuthorizedUsers
  | FetchNotAuthorizedUsersSuccess
  | FetchNotAuthorizedUsersFailure
  | FetchProducts
  | FetchProductsSuccess
  | FetchProductsFailure
  | UpdateUserAccess
  | UpdateUserAccessSuccess
  | UpdateUserAccessFailure
  | UpdateProduct
  | UpdateProductSuccess
  | UpdateProductFailure

export function adminGetUsers() {
  return async (dispatch: AppDispatch, getState: GetStateFunc): Promise<void> => {
    const token = ActionUtil.getToken(getState, 'adminGetUsers')
    if (!token) {
      return
    }

    dispatch<AdminAction>({type: AdminActionType.ADMIN_GET_USERS_PENDING})

    const usersRes = await AdminAPI.getUsers(token)
    if (usersRes.succeeded) {
      dispatch<AdminAction>(updateUsers(usersRes.payload.users))
    } else {
      const errMsg = ApiUtil.resolveLocalizedErrorMessage(usersRes)
      dispatch<AdminAction>(usersFetchFailed(errMsg))
    }
  }
}

/**
 * Get a list of users that has not (yet) been authorized to use the app.
 */
export function adminGetNotAuthorizedUsers() {
  return async (dispatch: AppDispatch, getState: GetStateFunc): Promise<void> => {
    const token = ActionUtil.getToken(getState, 'adminGetNotAuthorizedUsers')
    if (!token) {
      return
    }

    dispatch<AdminAction>({type: AdminActionType.ADMIN_GET_NOT_AUTHORIZED_USERS_PENDING})

    const usersRes = await AdminAPI.getNotAuthorizedUsers(token)
    if (usersRes.succeeded) {
      dispatch<AdminAction>(updateNotAuthorizedUsers(usersRes.payload.users))
    } else {
      const errMsg = ApiUtil.resolveLocalizedErrorMessage(usersRes)
      dispatch<AdminAction>(notAuthorizedUsersFetchFailed(errMsg))
    }
  }
}

export function adminGetProducts() {
  return async (dispatch: AppDispatch, getState: GetStateFunc): Promise<void> => {
    const token = ActionUtil.getToken(getState, 'adminGetProducts')
    if (!token) {
      return
    }

    dispatch<AdminAction>({type: AdminActionType.ADMIN_GET_PRODUCTS_PENDING})

    const productsRes = await AdminAPI.getProducts(token)
    if (productsRes.succeeded) {
      dispatch<AdminAction>(refreshProducts(productsRes.payload.products))
    } else {
      const errMsg = ApiUtil.resolveLocalizedErrorMessage(productsRes)
      dispatch<AdminAction>(productsFetchFailed(errMsg))
    }
  }
}

interface UserChangeOptions {
  // note: email cannot be changed
  email: string
  useAuthorized: boolean
  isAdmin: boolean
}

export function adminUpdateUserAccess(id: string, userOpts: UserChangeOptions) {
  return createOrUpdateUser(id, userOpts)
}

export function adminCreateNewUser(userOpts: UserChangeOptions) {
  return createOrUpdateUser(null, userOpts)
}

export function adminUpdateProduct(id: string, priceCents: number | null) {
  const payload = {
    priceCents
  }

  return async (dispatch: AppDispatch, getState: GetStateFunc): Promise<void> => {
    const token = ActionUtil.getToken(getState, 'adminUpdateProduct')
    if (!token) {
      return
    }

    dispatch<AdminAction>({type: AdminActionType.ADMIN_UPDATE_PRODUCT_PENDING})

    const updateRes = await AdminAPI.updateProduct(id, payload, token)
    if (updateRes.succeeded) {
      dispatch<AdminAction>(updatedProduct(updateRes.payload.product))
    } else {
      const errMsg = ApiUtil.resolveLocalizedErrorMessage(updateRes)
      dispatch<AdminAction>(updateProductFailed(errMsg))
    }
  }
}

/* Helper Functions */

function productsFetchFailed(error: string): AdminAction {
  return {type: AdminActionType.ADMIN_GET_PRODUCTS_REJECTED, payload: {error}}
}

function refreshProducts(products: AdminProductResponseItem[]): AdminAction {
  return {type: AdminActionType.ADMIN_GET_PRODUCTS_FULFILLED, payload: products}
}

function updateProductFailed(error: string): AdminAction {
  return {type: AdminActionType.ADMIN_UPDATE_PRODUCT_REJECTED, payload: {error}}
}

function updatedProduct(product: AdminProductResponseItem): AdminAction {
  return {type: AdminActionType.ADMIN_UPDATE_PRODUCT_FULFILLED, payload: product}
}

function usersFetchFailed(error: string): AdminAction {
  return {type: AdminActionType.ADMIN_GET_USERS_REJECTED, payload: {error}}
}

function updateUsers(users: AdminUserResponseItem[]): AdminAction {
  return {type: AdminActionType.ADMIN_GET_USERS_FULFILLED, payload: users}
}

function notAuthorizedUsersFetchFailed(error: string): AdminAction {
  return {type: AdminActionType.ADMIN_GET_NOT_AUTHORIZED_USERS_REJECTED, payload: {error}}
}

function updateNotAuthorizedUsers(users: AdminUserResponseItem[]): AdminAction {
  return {type: AdminActionType.ADMIN_GET_NOT_AUTHORIZED_USERS_FULFILLED, payload: users}
}

function updateUserPermissionsFailed(error: string): AdminAction {
  return {type: AdminActionType.ADMIN_UPDATE_USER_ACCESS_REJECTED, payload: {error}}
}

function updatedUserPermissions(user: AdminUserResponseItem): AdminAction {
  return {
    type: AdminActionType.ADMIN_UPDATE_USER_ACCESS_FULFILLED,
    payload: user
  }
}

function createOrUpdateUser(id: string | null, userOpts: UserChangeOptions) {
  const payload = {
    email: userOpts.email,
    useAuthorized: userOpts.useAuthorized,
    isAdmin: userOpts.isAdmin
  }

  const apiCallFunc: (token: string) => Promise<UserUpdateResponse> =
    // update
    id ? async (token) => await AdminAPI.updateUserPermissions(id, payload, token)
      // create
      : async (token) => await AdminAPI.createUser(payload, token)

  return async (dispatch: AppDispatch, getState: GetStateFunc): Promise<void> => {
    const token = ActionUtil.getToken(getState, 'adminUpdateUserAccess')
    if (!token) {
      return
    }

    dispatch<AdminAction>({type: AdminActionType.ADMIN_UPDATE_USER_ACCESS_PENDING})

    const updateRes = await apiCallFunc(token)
    if (updateRes.succeeded) {
      dispatch<AdminAction>(updatedUserPermissions(updateRes.payload.user))
    } else {
      const errMsg = ApiUtil.resolveLocalizedErrorMessage(updateRes)
      dispatch<AdminAction>(updateUserPermissionsFailed(errMsg ?? ''))
    }
  }
}
